import React, { useState } from 'react';
import { Button } from './ui/button';
import { services } from '../data/mock';
import { ArrowRight, Target, Layout, LineChart, Settings } from 'lucide-react';

const iconMap = {
  1: Target,
  2: Layout,
  3: LineChart,
  4: Settings,
};

const ServicesSection = ({ onCtaClick }) => {
  const [activeService, setActiveService] = useState(1);

  return (
    <section id="services" className="py-24 lg:py-32 bg-slate-950 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-rose-600/10 to-orange-600/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-bold text-orange-400 uppercase tracking-wider mb-4 px-4 py-2 bg-orange-500/10 rounded-full border border-orange-500/20">
            What We Do
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            Growth is a <span className="bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">system</span>,
            <br className="hidden md:block" />not a series of hacks.
          </h2>
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
            We build integrated growth machines. Every piece works together.
          </p>
        </div>

        {/* Services Tabs - Desktop */}
        <div className="hidden lg:grid grid-cols-4 gap-4 mb-8">
          {services.map((service) => {
            const IconComponent = iconMap[service.id];
            return (
              <button
                key={service.id}
                onClick={() => setActiveService(service.id)}
                className={`relative p-6 rounded-2xl text-left transition-all duration-300 ${
                  activeService === service.id
                    ? 'bg-white text-slate-900 scale-105 shadow-2xl'
                    : 'bg-white/5 text-white hover:bg-white/10'
                }`}
              >
                <IconComponent className={`w-8 h-8 mb-4 ${
                  activeService === service.id ? 'text-rose-500' : 'text-rose-400'
                }`} />
                <h3 className="font-bold text-lg mb-1">{service.title}</h3>
                <p className={`text-sm ${
                  activeService === service.id ? 'text-slate-600' : 'text-slate-400'
                }`}>
                  {service.tagline}
                </p>
                {activeService === service.id && (
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45" />
                )}
              </button>
            );
          })}
        </div>

        {/* Active Service Detail - Desktop */}
        <div className="hidden lg:block">
          {services.map((service) => (
            activeService === service.id && (
              <div
                key={service.id}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-10 border border-white/10 animate-fade-in"
              >
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  <div>
                    <h3 className="text-3xl font-black text-white mb-4">
                      {service.title}
                    </h3>
                    <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-4">
                      {service.capabilities.map((cap, index) => (
                        <li key={index} className="flex items-center gap-3 text-white">
                          <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${service.color}`} />
                          <span className="font-medium">{cap}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={`${service.bgColor} rounded-2xl p-10 text-center`}>
                    <div className="text-8xl mb-4">
                      {service.id === 1 ? '🎯' : service.id === 2 ? '💻' : service.id === 3 ? '📊' : '⚙️'}
                    </div>
                    <div className="text-slate-900 font-bold text-xl">{service.tagline}</div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>

        {/* Services Cards - Mobile */}
        <div className="lg:hidden space-y-4">
          {services.map((service) => {
            const IconComponent = iconMap[service.id];
            return (
              <div
                key={service.id}
                className={`${service.bgColor} rounded-2xl p-6`}
              >
                <IconComponent className="w-10 h-10 text-slate-900 mb-4" />
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.capabilities.map((cap, index) => (
                    <li key={index} className="flex items-center gap-2 text-slate-700 text-sm">
                      <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.color}`} />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <Button
            onClick={onCtaClick}
            className="group bg-white hover:bg-slate-100 text-slate-900 px-10 py-6 h-auto text-lg rounded-full font-bold transition-all hover:scale-105 hover:shadow-2xl"
          >
            Let's review your funnel
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
